import { INVITE_DAYS, InviteMember, type RoleOption } from '@cyberscore/contracts';
import { MailPlus, ShieldCheck, Users, UserX } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api';
import { useListState, usePage, type ListSpec } from '../../lib/list';
import { href } from '../../lib/router';
import { can, useSession } from '../../lib/session';
import { DataView, type Column } from '../../ui/DataView';
import { useToast } from '../../ui/Toasts';
import { usePeopleAction, type Member, type People } from './api';

const when = (iso: string) => new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }); // prettier-ignore
/** A role's name; one removed since shows as such. */
const nameOf = (roles: readonly RoleOption[], key: string) =>
  roles.find((r) => r.key === key)?.name ?? 'Removed role';
const failure = (e: unknown) =>
  e instanceof ApiError ? (e.issues[0]?.message ?? e.message) : 'Something went wrong';

/**
 * People (task 52): who is in this tenant, with what roles, and invitations. Someone invited joins when
 * they sign in with that email; an admin can change roles or suspend people, and one admin always remains.
 */
export const PEOPLE_LIST: ListSpec = { id: 'people', sort: 'email', dir: 'asc', filters: ['status', 'role'] };

/** `embedded`: inside Tenant 360's tab (task 102), under a smaller heading. */
export function PeoplePage({ embedded = false }: { embedded?: boolean } = {}) {
  const list = useListState(PEOPLE_LIST);
  const people = usePage<Member, People>('/tenants/current/people', list.state);
  return (
    <>
      <div className={`page-head${embedded ? ' embedded' : ''}`}>
        {embedded ? <h2>People</h2> : <h1>People</h1>}
        <p>
          Invite people by email with the roles they need. They join when they sign in with that email. Change roles or suspend someone here; the evidence pack’s access review lists who to look at.
        </p>
      </div>
      {people.data && (
        <>
          <Invite roles={people.data.assignable_roles} />
          {people.data.invitations.length > 0 && <Invitations list={people.data.invitations} roles={people.data.assignable_roles} />}
        </>
      )}
      <Members list={list} query={people} roles={people.data?.assignable_roles ?? []} />
      {!embedded && <RolesLink />}
    </>
  ); // prettier-ignore
}

/** What each role may do is on the Roles page (task 78). */
function RolesLink() {
  const { tenant } = useSession();
  if (!can(tenant, 'roles.view')) return null;
  return (
    <section className="card pad mt row-between wrap gap" aria-label="Roles">
      <span className="row gap"><ShieldCheck size={16} /> See what each role may do, side by side, and add roles of your own.</span>
      <a className="btn btn-sm" href={href.roles()}>Roles and permissions</a>
    </section>
  ); // prettier-ignore
}

function RolePicker({
  roles,
  value,
  onChange,
  id,
}: {
  roles: RoleOption[];
  value: string[];
  onChange: (r: string[]) => void;
  id: string;
}) {
  // prettier-ignore
  return (
    <div className="role-picker" role="group" aria-labelledby={id}>
      {roles.map((r) => (
        <label key={r.key} className="toggle-row">
          <input type="checkbox" checked={value.includes(r.key)} onChange={(e) => onChange(e.target.checked ? [...value, r.key] : value.filter((x) => x !== r.key))} />
          <span>{r.name}{!r.built_in && <span className="pill ok small-pill">Your own</span>}{r.description && <span className="hint block">{r.description}</span>}</span>
        </label>
      ))}
    </div>
  ); // prettier-ignore
}

function Invite({ roles }: { roles: RoleOption[] }) {
  const act = usePeopleAction();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = InviteMember.safeParse({ email, roles: picked });
    if (!body.success) return setError(body.error.issues[0]!.message);
    setError(null);
    try {
      await act.mutateAsync({ type: 'invite', body: body.data });
      toast(
        `Invited ${body.data.email}. Ask them to sign in with that email within ${INVITE_DAYS} days.`,
        'ok',
      );
      setEmail('');
      setPicked([]);
    } catch (err) {
      setError(failure(err));
    }
  };
  return (
    <form className="card form-card" onSubmit={submit} noValidate>
      <div className="card-head">
        <div className="card-title row gap"><MailPlus size={16} /> Invite someone</div>
      </div>
      <div className="form-body">
        <div className="field">
          <label htmlFor="inv-email">Email</label>
          <input id="inv-email" className="input" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com.my" />
          <span className="hint">They must sign in with this email address.</span>
        </div>
        <div className="field">
          <span className="field-label" id="inv-roles">Roles</span>
          <RolePicker id="inv-roles" roles={roles} value={picked} onChange={setPicked} />
        </div>
        {error && <div className="error" role="alert">{error}</div>}
      </div>
      <div className="form-foot">
        <button type="submit" className="btn btn-primary" disabled={act.isPending}>Send invitation</button>
      </div>
    </form>
  ); // prettier-ignore
}

function Invitations({ list, roles }: { list: People['invitations']; roles: RoleOption[] }) {
  const act = usePeopleAction();
  const toast = useToast();
  return (
    <section className="card mt" aria-labelledby="inv-open">
      <div className="card-head">
        <h2 id="inv-open" className="card-title">Waiting to sign in · {list.length}</h2>
      </div>
      <div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Email</th><th>Roles</th><th>Expires</th><th /></tr></thead>
          <tbody>
            {list.map((i) => (
              <tr key={i.id}>
                <td>{i.email}</td>
                <td className="small">{i.roles.map((r) => nameOf(roles, r)).join(', ')}</td>
                <td>{i.expired ? <span className="pill neutral">Expired</span> : <span className="muted">{when(i.expires_at)}</span>}</td>
                <td className="num">
                  <button type="button" className="btn btn-sm btn-ghost" disabled={act.isPending} onClick={() => act.mutate({ type: 'revoke', id: i.id }, { onError: (e) => toast(failure(e), 'bad') })}>
                    {i.expired ? 'Remove' : 'Revoke'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  ); // prettier-ignore
}

function Members({
  list,
  query,
  roles,
}: {
  list: ReturnType<typeof useListState>;
  query: ReturnType<typeof usePage<Member, People>>;
  roles: RoleOption[];
}) {
  const { me } = useSession();
  const [editing, setEditing] = useState<Member | null>(null);
  const columns: Column<Member>[] = [
    { key: 'email', label: 'Person', sort: 'email', render: (m) => (<>{m.email ?? m.name ?? m.user_id}{m.user_id === me?.user.id && <span className="muted small"> (you)</span>}{m.name && m.email && <div className="muted small">{m.name}</div>}</>) }, // prettier-ignore
    {
      key: 'roles',
      label: 'Roles',
      render: (m) => <span className="small">{m.roles.map((r) => nameOf(roles, r)).join(', ')}</span>,
    },
    { key: 'status', label: 'Status', render: (m) => <span className={`pill ${m.status === 'active' ? 'ok' : 'neutral'}`}>{m.status === 'active' ? 'Active' : 'Suspended'}</span> }, // prettier-ignore
    {
      key: 'last_seen',
      label: 'Last signed in',
      sort: 'last_seen',
      wide: true,
      render: (m) => <span className="muted">{when(m.last_seen_at)}</span>,
    },
    {
      key: 'joined',
      label: 'Joined',
      sort: 'joined',
      wide: true,
      render: (m) => <span className="muted">{when(m.created_at)}</span>,
    },
    { key: 'edit', label: '', align: 'right', render: (m) => <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(m)}>Change</button> }, // prettier-ignore
  ];
  return (
    <section className="mt" aria-labelledby="members-h">
      <h2 id="members-h" className="section-title">Members{query.data ? ` · ${query.data.total}` : ''}</h2>
      {editing && <EditMember key={editing.user_id} m={editing} roles={roles} onDone={() => setEditing(null)} />}
      <DataView
        list={list}
        query={query}
        noun="members"
        rowKey={(m) => m.user_id}
        columns={columns}
        filters={[
          { key: 'role', label: 'Roles', options: roles.map((r) => [r.key, r.name] as const) },
          { key: 'status', label: 'Statuses', options: [['active', 'Active'], ['suspended', 'Suspended']] },
        ]}
        sorts={[
          { value: 'email', label: 'Email', dir: 'asc' },
          { value: 'name', label: 'Name', dir: 'asc' },
          { value: 'last_seen', label: 'Last signed in', dir: 'desc' },
          { value: 'joined', label: 'Joined', dir: 'desc' },
        ]}
        searchLabel="Search people"
        empty={<><Users size={28} /><strong>No members yet</strong>Invite someone above.</>}
      />
    </section>
  ); // prettier-ignore
}

function EditMember({ m, roles, onDone }: { m: Member; roles: RoleOption[]; onDone: () => void }) {
  const act = usePeopleAction();
  const toast = useToast();
  const [picked, setPicked] = useState<string[]>(m.roles);
  const save = (body: { roles?: string[]; status?: 'active' | 'suspended' }) =>
    act.mutate(
      { type: 'update', userId: m.user_id, body },
      { onSuccess: onDone, onError: (e) => toast(failure(e), 'bad') },
    );
  return (
    <div className="card form-card editing" role="region" aria-label={`Change ${m.email ?? m.name ?? 'member'}`}>
      <div className="card-head">
        <div className="card-title">{m.email ?? m.name ?? m.user_id}</div>
      </div>
      <div className="form-body">
        <RolePicker id={`roles-${m.user_id}`} roles={roles} value={picked} onChange={setPicked} />
        <div className="row gap wrap mt">
          <button type="button" className="btn btn-sm btn-primary" disabled={act.isPending || !picked.length} onClick={() => save({ roles: picked })}>Save roles</button>
          {m.status === 'active' ? (
            <button type="button" className="btn btn-sm" disabled={act.isPending} onClick={() => save({ status: 'suspended' })}><UserX size={14} /> Suspend</button>
          ) : (
            <button type="button" className="btn btn-sm" disabled={act.isPending} onClick={() => save({ status: 'active' })}>Reactivate</button>
          )}
          <button type="button" className="btn btn-sm btn-ghost" onClick={onDone}>Cancel</button>
        </div>
      </div>
    </div>
  ); // prettier-ignore
}
